import React, { useState, useEffect } from 'react';
import { Shield, Key, Lock, Unlock, Trash2, AlertTriangle, Clock, Globe, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { authAPI, blockedIPsAPI } from '../../services/api';

const SecurityTab = () => {
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    loadBlockedIPs();
  }, []);

  const loadBlockedIPs = async () => {
    try {
      const response = await blockedIPsAPI.getAll();
      setBlockedIPs(response.data);
    } catch (error) {
      console.error('Error loading blocked IPs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Le password non corrispondono');
      return;
    }
    
    if (passwordForm.new_password.length < 8) {
      toast.error('La nuova password deve essere almeno 8 caratteri');
      return;
    }
    
    setChangingPassword(true);
    try {
      await authAPI.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password aggiornata con successo!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleUnblockIP = async (ip) => {
    if (!window.confirm(`Sei sicuro di voler sbloccare l'IP ${ip}?`)) return;
    
    try {
      await blockedIPsAPI.unblock(ip);
      toast.success(`IP ${ip} sbloccato`);
      loadBlockedIPs();
    } catch (error) {
      toast.error('Errore nello sblocco dell\'IP');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Sei sicuro di voler sbloccare tutti gli IP?')) return;
    
    try {
      await blockedIPsAPI.clearAll();
      toast.success('Tutti gli IP sono stati sbloccati');
      setBlockedIPs([]);
    } catch (error) {
      toast.error('Errore nello sblocco degli IP');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('it-IT');
  };

  const activeBlockedIPs = blockedIPs.filter(ip => ip.is_active);
  const expiredBlockedIPs = blockedIPs.filter(ip => !ip.is_active);

  return (
    <div className="space-y-6">
      {/* Change Password Card */}
      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Key className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Cambia Password</CardTitle>
              <CardDescription>Modifica la password del tuo account admin</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <Label htmlFor="current_password">Password Attuale</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="current_password"
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  required
                  placeholder="••••••••"
                  className="pl-10"
                  data-testid="current-password-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="new_password">Nuova Password</Label>
              <div className="relative mt-2">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="new_password"
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  required
                  placeholder="••••••••"
                  className="pl-10"
                  data-testid="new-password-input"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Minimo 8 caratteri</p>
            </div>
            <div>
              <Label htmlFor="confirm_password">Conferma Nuova Password</Label>
              <div className="relative mt-2">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  required
                  placeholder="••••••••"
                  className="pl-10"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            <Button type="submit" disabled={changingPassword} data-testid="change-password-btn">
              {changingPassword ? 'Aggiornamento...' : 'Aggiorna Password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* IP Lockout Info Card */}
      <Card className="glass-card bg-amber-500/5 border-amber-500/20">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-500">Protezione Brute Force Attiva</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Il sistema blocca automaticamente gli IP dopo <strong>5 tentativi di login falliti</strong> per <strong>4 ore</strong>.
                Quando un IP viene bloccato, riceverai un&apos;email di alert con i dettagli dell&apos;attacco.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Blocked IPs Card */}
      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <CardTitle className="text-2xl">IP Bloccati</CardTitle>
                <CardDescription>Gestisci gli IP bloccati per tentativi di login falliti</CardDescription>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={loadBlockedIPs}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Aggiorna
              </Button>
              {blockedIPs.length > 0 && (
                <Button variant="destructive" size="sm" onClick={handleClearAll}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Sblocca Tutti
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Caricamento...</div>
          ) : blockedIPs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nessun IP bloccato</p>
              <p className="text-sm">Il sistema è sicuro</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Active Blocked IPs */}
              {activeBlockedIPs.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-500 mb-3 flex items-center">
                    <Lock className="w-4 h-4 mr-2" />
                    IP Attualmente Bloccati ({activeBlockedIPs.length})
                  </h4>
                  <div className="rounded-lg border border-red-500/20 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Indirizzo IP</TableHead>
                          <TableHead>Email Tentata</TableHead>
                          <TableHead>Tentativi</TableHead>
                          <TableHead>Bloccato il</TableHead>
                          <TableHead>Scade il</TableHead>
                          <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activeBlockedIPs.map((ip) => (
                          <TableRow key={ip.ip_address} className="bg-red-500/5">
                            <TableCell className="font-mono font-medium">
                              <div className="flex items-center space-x-2">
                                <Globe className="w-4 h-4 text-red-500" />
                                <span>{ip.ip_address}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-amber-500">{ip.last_attempt_email || '-'}</TableCell>
                            <TableCell>
                              <span className="px-2 py-1 rounded-full bg-red-500/20 text-red-500 text-xs font-medium">
                                {ip.attempts} tentativi
                              </span>
                            </TableCell>
                            <TableCell className="text-sm">{formatDate(ip.blocked_at)}</TableCell>
                            <TableCell className="text-sm">
                              <div className="flex items-center space-x-1 text-amber-500">
                                <Clock className="w-3 h-3" />
                                <span>{formatDate(ip.expires_at)}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUnblockIP(ip.ip_address)}
                                className="text-green-500 hover:text-green-600"
                              >
                                <Unlock className="w-4 h-4 mr-1" />
                                Sblocca
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Expired Blocked IPs (History) */}
              {expiredBlockedIPs.length > 0 && (
                <div>
                  <h4 className="font-medium text-muted-foreground mb-3 flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Storico Blocchi Scaduti ({expiredBlockedIPs.length})
                  </h4>
                  <div className="rounded-lg border border-border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Indirizzo IP</TableHead>
                          <TableHead>Email Tentata</TableHead>
                          <TableHead>Tentativi</TableHead>
                          <TableHead>Bloccato il</TableHead>
                          <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {expiredBlockedIPs.map((ip) => (
                          <TableRow key={ip.ip_address}>
                            <TableCell className="font-mono text-muted-foreground">
                              <div className="flex items-center space-x-2">
                                <Globe className="w-4 h-4" />
                                <span>{ip.ip_address}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{ip.last_attempt_email || '-'}</TableCell>
                            <TableCell className="text-muted-foreground">{ip.attempts}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{formatDate(ip.blocked_at)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUnblockIP(ip.ip_address)}
                                className="text-muted-foreground hover:text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityTab;
